import { BaserowClient } from '../../client/baserow-client';
import type { Field, Table } from '../../types/database';
import { writeFileSync, mkdirSync, existsSync } from 'fs';
import { join, dirname } from 'path';
import type { ResolvedConfig } from '../config/types';
import { AuthError } from '../utils/client';

/**
 * Represents a select option for a Baserow field.
 */
interface SelectOption {
  value: string;
  color: string;
  id: number;
}

/**
 * Represents a Baserow field with select options.
 */
interface FieldWithOptions extends Field {
  select_options?: SelectOption[];
}

/**
 * Maps a Baserow field to a TypeScript type.
 * @param field The field to map.
 * @returns The TypeScript type as a string.
 */
function generateTypeForField(field: FieldWithOptions): string {
  // Map Baserow field types to TypeScript types
  switch (field.type) {
    case 'text':
    case 'long_text':
    case 'url':
    case 'email':
    case 'phone_number':
      return 'string';
    case 'number':
    case 'rating':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'date':
      return 'Date';
    case 'file':
      return '{ url: string; name: string; }[]';
    case 'single_select':
      return field.select_options?.map((opt: SelectOption) => `'${opt.value}'`).join(' | ') || 'string';
    case 'multiple_select':
      return `Array<${field.select_options?.map((opt: SelectOption) => `'${opt.value}'`).join(' | ') || 'string'}>`;
    case 'link_row':
      return `number[]`; // Array of related row IDs
    default:
      return 'any';
  }
}

/**
 * Sanitizes a field name for use as a TypeScript property
 * @param name The field name to sanitize
 * @returns A sanitized, properly quoted field name
 */
function sanitizeFieldName(name: string): string {
  // If the name contains spaces, special characters, or starts with a number,
  // wrap it in quotes to make it a valid TypeScript property name
  if (!/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(name)) {
    // Escape any quotes in the name
    const escapedName = name.replace(/'/g, "\\'");
    return `'${escapedName}'`;
  }
  return name;
}

/**
 * Generates a TypeScript interface for a Baserow table.
 * @param table The table definition.
 * @param fields The fields of the table.
 * @returns The TypeScript interface as a string.
 */
function generateInterfaceForTable(table: Table, fields: Field[]): string {
  const fieldDefinitions = fields
    .map(field => {
      const isOptional = !field.primary && field.type !== 'formula';
      const fieldName = sanitizeFieldName(field.name);
      return `  ${fieldName}${isOptional ? '?' : ''}: ${generateTypeForField(field as FieldWithOptions)};`;
    })
    .join('\n');

  // Create a valid TypeScript interface name by removing non-alphanumeric characters
  const interfaceName = table.name.replace(/[^a-zA-Z0-9]/g, '') + 'Row';

  return `export interface ${interfaceName} {
  id: number;
  order: string;
${fieldDefinitions}
}`;
}

/**
 * Ensures a directory exists, creating it if necessary
 * @param path Directory path to ensure exists
 */
function ensureDirectoryExists(path: string): void {
  const dir = dirname(path);
  if (!existsSync(dir)) {
    // Create the directory recursively (like mkdir -p)
    mkdirSync(dir, { recursive: true });
  }
}

/**
 * Generates TypeScript types from a Baserow database.
 *
 * @param config The resolved configuration for Baserow connection and output.
 * @param options Optional settings: { writeToDisk?: boolean }.
 * @returns The generated type definitions as a string.
 * @throws If required configuration is missing or an error occurs during generation.
 */
export async function generateTypes(
  config: ResolvedConfig,
  options?: { writeToDisk?: boolean }
): Promise<string> {
  try {
    if (!config.url || !config.token || !config.database) {
      throw new Error('Missing required configuration: url, token, and database are required');
    }

    const client = new BaserowClient({
      url: config.url,
      token: config.token,
      tokenType: config.tokenType
    });

    // Try to fetch tables
    let tables;
    try {
      tables = await client.databaseTables.list(parseInt(config.database));
    } catch (err: any) {
      // Check for auth error
      if (
        err instanceof AuthError ||
        (err && err.message && (
          err.message.includes('401') ||
          err.message.includes('403') ||
          err.message.toLowerCase().includes('unauthorized') ||
          err.message.toLowerCase().includes('jwt') ||
          err.message.toLowerCase().includes('token')
        ))
      ) {
        throw new Error(
          'Authentication failed: Generating types requires a valid JWT token. ' +
          'Please run `baserow login` to obtain a JWT token and try again.'
        );
      }
      throw err;
    }

    let typeDefinitions = '// Generated by baserow-cli\n\n';

    // Filter tables if specific ones are requested
    const tablesToProcess = config.tables?.length
      ? tables.filter(t => config.tables?.includes(t.name))
      : tables;

    // For each table, fetch its fields and generate an interface
    for (const table of tablesToProcess) {
      const fields = await client.databaseFields.list(table.id);
      typeDefinitions += generateInterfaceForTable(table, fields) + '\n\n';
    }

    if (options?.writeToDisk !== false) {
      // Determine output path
      const outputPath = config.output?.startsWith('/')
        ? config.output
        : join(process.cwd(), config.output || 'baserow-types.ts');
      
      // Ensure the directory exists
      ensureDirectoryExists(outputPath);
      
      // Write the type definitions file
      writeFileSync(outputPath, typeDefinitions);
    }

    return typeDefinitions;
  } catch (error) {
    throw error;
  }
} 